/**
 * Browser-side categorization engine. Operates over already-decrypted
 * transactions (React Query cache) plus the household's learning bag.
 * Returns plans the UI can preview and apply via bulk-link-entity +
 * patch-transactions.
 */

import { type ResolveContext, type ResolvedEntity, resolveEntity } from "@paisa/reconciliation"
import type { CategoryRule, LearningBag } from "@/hooks/use-learning"
import type { TransactionRow } from "@/hooks/use-transactions"

export interface RescanInput {
  /** The transaction the user just edited (it already has the new entity set). */
  source: { id: string; description: string }
  /** Decrypted unmatched transactions in the same household. */
  unmatched: Array<Pick<TransactionRow, "id" | "description">>
  entity: { type: "merchant"; merchantId: string } | { type: "person"; personId: string }
  /** Decrypted entities for fuzzball fallback (optional but useful). */
  ctxExtras: Pick<ResolveContext, "globalMerchants" | "localMerchants" | "persons">
  /** The household's learning bag — includes any newly-added alias. */
  bag: LearningBag
}

export interface RescanResult {
  /** Transaction IDs (excluding the source) that the new alias picks up. */
  matchedIds: string[]
  /** The pattern derived from the source description that triggered the match. */
  pattern: string
}

/**
 * Run the new alias against the household's other unmatched transactions.
 * Used by UX moment 2 (typing a new merchant/person on a transaction).
 *
 * The caller is expected to have already added the alias to `bag` before
 * calling this. We re-run resolveEntity to surface every unmatched row that
 * resolves to the same entity — including matches via the global/hardcoded
 * alias table or fuzzball, not just the user-taught alias.
 */
export function rescanUnmatchedForEntity(input: RescanInput): RescanResult {
  const ctx: ResolveContext = {
    globalMerchants: input.ctxExtras.globalMerchants,
    localMerchants: input.ctxExtras.localMerchants,
    persons: input.ctxExtras.persons,
    userMerchantAliases: input.bag.merchantAliases,
    userPersonAliases: input.bag.personAliases,
  }

  const matchedIds: string[] = []
  for (const row of input.unmatched) {
    if (row.id === input.source.id) continue
    const resolved = resolveEntity(row.description, "unknown", ctx)
    if (!resolved) continue
    if (sameEntity(resolved, input.entity)) {
      matchedIds.push(row.id)
    }
  }
  return { matchedIds, pattern: deriveAliasPattern(input.source.description) }
}

interface AutoCategorizeRow {
  id: string
  description: string
  merchantId: string | null
  personId: string | null
  categoryId: string | null
  isTransfer: boolean
  isIgnored: boolean
}

interface AutoCategorizeSuggestion {
  entity: { type: "merchant"; merchantId: string } | { type: "person"; personId: string }
  /** "category-rule" if the entity already has a household rule; "alias-only"
   * if the description was matched but no category rule exists yet. */
  source: "category-rule" | "alias-only"
  categoryId: string | null
  /** The transaction IDs that this suggestion applies to. */
  transactionIds: string[]
}

export interface AutoCategorizePlan {
  suggestions: AutoCategorizeSuggestion[]
  /** Total rows the plan can act on. */
  totalAffected: number
  /** Rows that remained unmatched. */
  unresolvedCount: number
}

export interface AutoCategorizeContext {
  rows: AutoCategorizeRow[]
  bag: LearningBag
  globalMerchants: ResolveContext["globalMerchants"]
  localMerchants: ResolveContext["localMerchants"]
  persons: ResolveContext["persons"]
  /** Merchant id → built-in default category; the fallback when the household has no rule. */
  merchantCategoryById?: Record<string, string>
}

/**
 * Build an auto-categorize preview over the household's transactions.
 *
 * Two contributions per row:
 *   1. If a row already has a merchantId/personId but no categoryId, look up
 *      the household rule for that entity and propose the rule's category.
 *   2. If a row has no merchantId/personId, run resolveEntity over its
 *      description and propose the resulting entity (and the entity's rule
 *      category if one exists).
 *
 * The result is grouped by (entity, source) so the UI can render one row per
 * group with a count.
 */
export function autoCategorizeFromDescriptions(ctx: AutoCategorizeContext): AutoCategorizePlan {
  const resolveCtx: ResolveContext = {
    globalMerchants: ctx.globalMerchants,
    localMerchants: ctx.localMerchants,
    persons: ctx.persons,
    userMerchantAliases: ctx.bag.merchantAliases,
    userPersonAliases: ctx.bag.personAliases,
  }

  const groups = new Map<string, AutoCategorizeSuggestion>()
  let unresolvedCount = 0
  let totalAffected = 0

  for (const row of ctx.rows) {
    // Transfers and ignored rows must never be auto-linked — they corrupt spend totals.
    if (row.isTransfer || row.isIgnored) continue

    let entity: ResolvedEntity = null
    if (row.merchantId) entity = { type: "merchant", merchantId: row.merchantId }
    else if (row.personId) entity = { type: "person", personId: row.personId }
    else {
      entity = resolveEntity(row.description, "unknown", resolveCtx)
    }

    if (!entity) {
      unresolvedCount += 1
      continue
    }

    const rule = entityRule(entity, ctx.bag, ctx.merchantCategoryById)
    if (row.categoryId && rule && rule.categoryId === row.categoryId) {
      // Already in good shape — no work for this row.
      continue
    }
    // We can act if either (a) the entity has a rule and the row's category
    // doesn't match, OR (b) the row has no entity yet and we just resolved one.
    const needsLink = !row.merchantId && !row.personId
    const needsCategory = !row.categoryId && rule != null
    if (!needsLink && !needsCategory) continue

    const key = entityKey(entity)
    const existing = groups.get(key)
    const suggestion: AutoCategorizeSuggestion = existing ?? {
      entity,
      source: rule ? "category-rule" : "alias-only",
      categoryId: rule?.categoryId ?? null,
      transactionIds: [],
    }
    suggestion.transactionIds.push(row.id)
    groups.set(key, suggestion)
    totalAffected += 1
  }

  return {
    suggestions: Array.from(groups.values()),
    totalAffected,
    unresolvedCount,
  }
}

// ─── helpers ────────────────────────────────────────────────────────────────

function sameEntity(a: ResolvedEntity, b: NonNullable<ResolvedEntity>): boolean {
  if (!a) return false
  if (a.type !== b.type) return false
  if (a.type === "merchant" && b.type === "merchant") return a.merchantId === b.merchantId
  if (a.type === "person" && b.type === "person") return a.personId === b.personId
  return false
}

function entityKey(e: NonNullable<ResolvedEntity>): string {
  if (e.type === "merchant") return `m:${e.merchantId}`
  return `p:${e.personId}`
}

/** Household rule first; for merchants, fall back to the merchant's built-in default category. */
function entityRule(
  e: NonNullable<ResolvedEntity>,
  bag: LearningBag,
  merchantCategoryById: Record<string, string> = {},
): CategoryRule | null {
  if (e.type === "merchant") {
    const fallback = merchantCategoryById[e.merchantId]
    return (
      bag.merchantRules[e.merchantId] ??
      (fallback ? { categoryId: fallback, lastUserCategoryAt: "" } : null)
    )
  }
  return bag.personRules[e.personId] ?? null
}

/**
 * Derive a plain alias pattern from a transaction description. The simplest
 * useful pattern is the lowercased, normalised form — same shape the
 * reconciliation engine matches against.
 */
export function deriveAliasPattern(description: string): string {
  return description
    .toLowerCase()
    .replace(/\b(private|pvt\.?|limited|ltd\.?)\s*\w*/gi, "")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .slice(0, 3) // first 1-3 normalised words — keeps patterns specific
    .join(" ")
}
